import { useEffect, useRef } from 'react';
import { Attribution } from 'ol/control';
import OLMousePositionControl from 'ol/control/MousePosition';
import OLScaleLineControl from 'ol/control/ScaleLine';
import * as olProj from 'ol/proj';
import { MapWidgetRootStore } from './MapWidgetRootStore';
import { MapWidgetContext } from './MapWidgetContext';
import { BottomActions } from './BottomActions';
import { NewsInitializer } from './News';
import { EventsInitializer } from './Events';
import { Coordinate } from 'ol/coordinate';
import { ErrorBoundary } from '../../../../components/ErrorBoundary';
import { ProjectionActivator } from '../../../../components/Map/ProjectionActivator';

interface MapWidgetProps {
  zoom: number;
  center: number[];
  enableRotation: boolean;
  initialRotation: number;
  legend: boolean;
  card: boolean;
  layers: string[];
  news: boolean;
  events: boolean;
}

export const MapWidget = ({
  zoom,
  center,
  enableRotation,
  initialRotation,
  layers,
  legend,
  card,
  news,
  events,
}: MapWidgetProps) => {
  const mapWidgetStore = useRef(
    new MapWidgetRootStore(zoom, center, enableRotation, initialRotation),
  );
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (mapRef.current) {
      const store = mapWidgetStore.current;

      store.map.mapProperties.target = mapRef.current;

      const attribution = new Attribution({
        className: 'attributions',
      });

      const map = store.map.mapProperties.map;

      map.addControl(attribution);
      map.addControl(new OLScaleLineControl());
      map.addControl(
        new OLMousePositionControl({
          coordinateFormat(coords: Coordinate | undefined): string {
            if (typeof coords === 'undefined') {
              return '';
            }

            const projCode = store.map.mapProperties.map
              .getView()
              .getProjection()
              .getCode();
            const coordinates = olProj.transform(coords, projCode, 'EPSG:4326');

            return `${coordinates[1]}, ${coordinates[0]}`;
          },
          className: 'mouse-position',
          placeholder: '',
        }),
      );
    }
  }, []);

  useEffect(() => {
    const store = mapWidgetStore.current;

    const load = async (layers: string[]) => {
      await store.baseLayers.load();

      const donlandLayer = 'Donland_gisro_map';

      if (layers.length === 0 || layers.includes(donlandLayer)) {
        store.baseLayers.setActiveLayer(donlandLayer);
      }

      layers.forEach((layer) => {
        store.map.apiMap.addLayer(layer);
      });
    };

    if (mapRef.current) load(layers);
  }, [layers]);

  const store = mapWidgetStore.current;

  return (
    <MapWidgetContext.Provider value={store}>
      <ProjectionActivator>
        <div id="map-widget" ref={mapRef}></div>
      </ProjectionActivator>

      <ErrorBoundary>
        <NewsInitializer status={news ? 'enabled' : 'disabled'}>
          <EventsInitializer status={events ? 'enabled' : 'disabled'}>
            <BottomActions isCardVisible={card} isLegendVisible={legend} />
          </EventsInitializer>
        </NewsInitializer>
      </ErrorBoundary>
    </MapWidgetContext.Provider>
  );
};
